import React, { FC } from 'react'

import { formatAirport } from '../../transforms/airport'
import { Transfer, TransferDirection, TransferStatus } from '../../types'
import { format as formatDate, isAfter, isBefore, parseISO } from 'date-fns'
import { Config, PageURLs } from '../../common'
import ListGroup from '../ListGroup'
import EditableTransferDetailRow from './EditableTransferDetailRow'
import DetailRow from './DetailRow'
import { Link } from 'react-router-dom'
import Icons from '../Icons'
import Alert from '../Alert'

interface Props {
  transfer: Transfer
  onUpdated?: (transfer: Transfer) => void
  onCancel?: (transfer: Transfer) => void
}

const TransferDetail: FC<Props> = ({ transfer, onUpdated, onCancel }) => {
  const address = transfer.address_formatted
  const airport = formatAirport(transfer)
  const isToAirport = transfer.direction === TransferDirection.ToAirport
  const [origin, destination] = isToAirport ? [address, airport] : [airport, address]
  const pickupDateTime = parseISO(transfer.pickup_datetime)
  const isCancelled = transfer.status === TransferStatus.Cancelled
  const isConfirmed = transfer.status === TransferStatus.Confirmed
  const isPending = transfer.status === TransferStatus.Pending
  const isReserved = transfer.status === TransferStatus.Reserved
  const isPast = isBefore(pickupDateTime, new Date())
  const isFuture = isAfter(pickupDateTime, new Date())
  const formattedDate = formatDate(pickupDateTime, Config.DateFormat)
  const formattedTime = formatDate(pickupDateTime, Config.TimeFormat)

  return (
    <>
      <ListGroup.Items>
        <ListGroup.Item>
          <div className="px-2 px-lg-4 pt-3 pb-2 pt-lg-4 pb-lg-3 mt-4">
            <h5>Booking</h5>
            <h3 className="h1">#{transfer.booking_id}</h3>
            <span className="bg-primary px-2 py-1">{isCancelled ? 'cancelled' : isPast ? 'past' : 'upcoming'}</span>

            <div className="booking-details-items">
              <div className="mt-4">
                <span className="placeholder-text d-inline-block w-100 mb-2">From</span>
                <span className="d-inline-block w-100">{origin}</span>
              </div>
              <div className="mt-4">
                <span className="placeholder-text d-inline-block w-100 mb-2">To</span>
                <span className="d-inline-block w-100">{destination}</span>
              </div>
              <div className="mt-4">
                <span className="placeholder-text d-inline-block w-100 mb-2">Pickup</span>
                <span className="d-inline-block w-100">
                  {formattedDate} at {formattedTime}
                </span>
              </div>
              <div className="mt-4">
                <span className="placeholder-text d-inline-block w-100 mb-2">Contact</span>
                <span className="d-inline-block w-100">
                  {transfer.contact_first_name} {transfer.contact_last_name}
                </span>
              </div>

              {isFuture && isConfirmed ? (
                <>
                  <EditableTransferDetailRow
                    onUpdated={onUpdated}
                    transfer={transfer}
                    attribute="contact_email"
                    label="Email"
                  />
                  <EditableTransferDetailRow
                    onUpdated={onUpdated}
                    transfer={transfer}
                    attribute="contact_phone"
                    label="Phone"
                  />
                </>
              ) : (
                <>
                  <DetailRow className="mt-4" label="Email" value={transfer.contact_email} />
                  <DetailRow className="mt-4" label="Phone" value={transfer.contact_phone} />
                </>
              )}
            </div>
          </div>
        </ListGroup.Item>
        {transfer.supplier_name && (
          <ListGroup.Item>
            <div className="px-2 px-lg-4 pt-3 pb-2 pt-lg-4 pb-lg-3">
              <div className="booking-details-items">
                <div className="booking-details-row">
                  <span className="placeholder-text d-inline-block w-100 mb-3">Your driver</span>
                  <h5 className="h5">{transfer.supplier_name}</h5>
                  {transfer.supplier_phone_number && <p>{transfer.supplier_phone_number}</p>}
                </div>
              </div>
            </div>
          </ListGroup.Item>
        )}

        {transfer.supplier_notes && (
          <ListGroup.Item>
            <div className="px-2 px-lg-4 pt-3 pb-2 pt-lg-4 pb-lg-3">
              <div className="booking-details-items">
                <div className="booking-details-row">
                  <span className="placeholder-text d-inline-block w-100 mb-3">Instructions</span>
                  <div dangerouslySetInnerHTML={{ __html: transfer.supplier_notes }}></div>
                </div>
              </div>
            </div>
          </ListGroup.Item>
        )}
        <ListGroup.Item>
          <Link to={`${PageURLs.Billing}?order_id=${transfer.order_id}`} className="receipt__link">
            <span className="d-flex align-items-center">
              <Icons.Receipt />
              <span style={{ marginLeft: '20px' }}>Receipt</span>
            </span>
            <Icons.Arrow fill={'#F1F9FB'} />
          </Link>
        </ListGroup.Item>
        {isFuture && (
          <ListGroup.Item>
            <div className="px-2 px-lg-4 pt-3 pb-2 pt-lg-4 pb-lg-3">
              <div className="booking-details-items">
                {isConfirmed && (
                  <div className="text-right">
                    <button
                      className="btn btn-outline btn-link btn-sm text-black-50"
                      type="button"
                      onClick={() => onCancel?.(transfer)}
                    >
                      <span>Cancel Booking</span>
                    </button>
                  </div>
                )}

                {(isPending || isReserved) && (
                  <>
                    <Alert type={'info'} msg={'Cancellation will be available once we have confirmed your transfer.'} />
                  </>
                )}
              </div>
            </div>
          </ListGroup.Item>
        )}
      </ListGroup.Items>
    </>
  )
}

export default TransferDetail
